/**
 * Full-viewport idle overlay. After inactivity on homepage, collection,
 * and collections-list templates, one merchant-selected engine runs: Drawing, Ping Pong, or
 * Stamped. Any pointer, key, scroll, or focus activity dismisses it and
 * restarts the timer. CSS + JS are only loaded when the setting is on.
 */
if (!customElements.get('idle-animation')) {
  class IdleAnimation extends HTMLElement {
    static ACTIVITY_EVENTS = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'touchmove',
      'pointerdown',
      'focusin',
    ];

    static BOUNCE_SPEED_X = 2.5;
    static BOUNCE_SPEED_Y = 2.1;
    static BOUNCE_MOBILE_SCALE = 0.58;
    static BOUNCE_MOBILE_MQ = '(max-width: 749px)';
    static SQUASH_MS = 180;
    static SQUASH_COMPRESS = 0.86;
    static SQUASH_STRETCH = 1.08;
    static STAMP_MAX = 300;
    static STAMP_MIN_DELAY = 700;
    static STAMP_MAX_DELAY = 1200;
    static STAMP_SCALE_MIN = 1.15;
    static STAMP_SCALE_MAX = 2.35;
    static STAMP_ROTATION_MIN = -75;
    static STAMP_ROTATION_MAX = 75;
    static STAMP_PLACE_ATTEMPTS = 16;
    static DRAWABLE = 'path, polygon, polyline, rect, circle, ellipse, line';
    static DRAW_EASING = 'cubic-bezier(0.65, 0, 0.35, 1)';
    static DRAW_MS_PER_PX = 0.42;
    static DRAW_MIN_MS = 1800;
    static DRAW_MAX_MS = 4200;
    static REVERSE_MS_PER_PX = 0.28;
    static REVERSE_MIN_MS = 1100;
    static REVERSE_MAX_MS = 2800;
    static FILL_HANDOFF_MS = 450;
    static CTA_HOLD_MS = 7000;
    static PRE_REVERSE_MS = 1500;
    static LOOP_GAP_MS = 500;
    static STROKE_VIEWBOX_RATIO = 427;
    static POINTER_MOVE_MIN_PX = 2;

    connectedCallback() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      this.delay = Number(this.dataset.idleDelay) || 10000;
      this.effect = this.dataset.idleEffect;
      this.idleTimer = null;
      this.bounceFrame = null;
      this.drawReady = false;
      this.drawSequenceId = 0;
      this.drawPreparePromise = null;
      this.lastPointerX = null;
      this.lastPointerY = null;
      this.boundReset = this.onUserActivity.bind(this);
      this.boundResize = this.onResize.bind(this);

      IdleAnimation.ACTIVITY_EVENTS.forEach((eventName) => {
        window.addEventListener(eventName, this.boundReset, { passive: true });
      });
      window.addEventListener('resize', this.boundResize, { passive: true });

      if (this.effect === 'logo_draw') {
        this.prepareDraw();
      }

      this.resetIdleTimer();
    }

    disconnectedCallback() {
      if (!this.boundReset) return;

      clearTimeout(this.idleTimer);
      this.stopBounce();
      this.stopStamp();
      this.stopDraw();
      IdleAnimation.ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, this.boundReset);
      });
      window.removeEventListener('resize', this.boundResize);
    }

    /* Hero autoplay retriggers mousemove when slides change under a still cursor. */
    onUserActivity(event) {
      if (event.isTrusted === false) return;

      if (event.type === 'mousemove' || event.type === 'pointermove' || event.type === 'touchmove') {
        const point = event.touches && event.touches[0] ? event.touches[0] : event;
        const x = point.clientX;
        const y = point.clientY;
        if (Number.isFinite(x) && Number.isFinite(y)) {
          if (this.lastPointerX != null && this.lastPointerY != null) {
            const dx = x - this.lastPointerX;
            const dy = y - this.lastPointerY;
            const min = IdleAnimation.POINTER_MOVE_MIN_PX;
            if (dx * dx + dy * dy < min * min) return;
          }
          this.lastPointerX = x;
          this.lastPointerY = y;
        }
      }

      if (event.type === 'focusin' && event.target && event.target.id === 'MainContent') {
        return;
      }

      this.resetIdleTimer();
    }

    resetIdleTimer() {
      this.hide();
      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => this.show(), this.delay);
    }

    show() {
      this.classList.add('is-active');
      this.setAttribute('aria-hidden', 'false');
      this.startEngine();
    }

    hide() {
      this.stopEngine();
      if (!this.classList.contains('is-active')) return;
      this.classList.remove('is-active');
      this.setAttribute('aria-hidden', 'true');
    }

    startEngine() {
      if (this.effect === 'logo_bounce') this.startBounce();
      if (this.effect === 'logo_stamp') this.startStamp();
      if (this.effect === 'logo_draw') this.startDraw();
    }

    stopEngine() {
      this.stopBounce();
      this.stopStamp();
      this.stopDraw();
    }

    /* Ping Pong — frame-independent bounce, slower on small viewports. */
    bounceSpeedScale() {
      return window.matchMedia(IdleAnimation.BOUNCE_MOBILE_MQ).matches
        ? IdleAnimation.BOUNCE_MOBILE_SCALE
        : 1;
    }

    applyBounceSpeed(preserveDirection = false) {
      const scale = this.bounceSpeedScale();
      const signX = preserveDirection && this.velocityX < 0 ? -1 : 1;
      const signY = preserveDirection && this.velocityY < 0 ? -1 : 1;
      this.velocityX = signX * IdleAnimation.BOUNCE_SPEED_X * scale;
      this.velocityY = signY * IdleAnimation.BOUNCE_SPEED_Y * scale;
    }

    onResize() {
      if (this.effect === 'logo_bounce') {
        this.sizeBounceLogo();
        if (this.classList.contains('is-active')) {
          this.applyBounceSpeed(true);
          this.resetBouncePosition();
        }
      }

      if (this.effect === 'logo_draw' && this.drawSizerSvg) {
        this.sizeDrawLogo(this.drawSizerSvg);
      }
    }

    startBounce() {
      this.bounceLogo = this.querySelector('.idle-bounce-logo');
      this.bounceImage = this.querySelector('.idle-bounce-image');
      if (!this.bounceLogo || !this.bounceImage) return;

      const run = () => {
        if (!this.classList.contains('is-active')) return;
        this.sizeBounceLogo();
        this.applyBounceSpeed();
        this.squashX = 1;
        this.squashY = 1;
        this.squashUntil = 0;
        this.lastTimestamp = 0;
        this.resetBouncePosition();
        this.stopBounce();
        this.bounceFrame = requestAnimationFrame((time) => this.runBounce(time));
      };

      if (this.bounceImage.complete && this.bounceImage.naturalWidth) {
        run();
      } else {
        this.bounceImage.addEventListener('load', run, { once: true });
      }
    }

    stopBounce() {
      if (this.bounceFrame) cancelAnimationFrame(this.bounceFrame);
      this.bounceFrame = null;
      this.lastTimestamp = 0;
    }

    sizeBounceLogo() {
      if (!this.bounceLogo || !this.bounceImage) return;

      const naturalWidth = this.bounceImage.naturalWidth;
      const naturalHeight = this.bounceImage.naturalHeight;
      if (!naturalWidth || !naturalHeight) return;

      const ratio = naturalWidth / naturalHeight;
      if (ratio >= 4) {
        this.bounceLogo.style.width = 'clamp(240px, 30vw, 480px)';
      } else if (ratio >= 2) {
        this.bounceLogo.style.width = 'clamp(220px, 26vw, 400px)';
      } else if (ratio >= 1) {
        this.bounceLogo.style.width = 'clamp(170px, 20vw, 300px)';
      } else {
        this.bounceLogo.style.width = 'clamp(130px, 16vw, 230px)';
      }
    }

    resetBouncePosition() {
      if (!this.bounceLogo) return;

      const logoWidth = this.bounceLogo.offsetWidth;
      const logoHeight = this.bounceLogo.offsetHeight;
      this.bounceX = Math.max(20, (window.innerWidth - logoWidth) * 0.2);
      this.bounceY = Math.max(20, (window.innerHeight - logoHeight) * 0.25);
      this.applyBounceTransform(1, 1);
    }

    squish(verticalWall, timestamp) {
      this.squashX = verticalWall ? IdleAnimation.SQUASH_COMPRESS : IdleAnimation.SQUASH_STRETCH;
      this.squashY = verticalWall ? IdleAnimation.SQUASH_STRETCH : IdleAnimation.SQUASH_COMPRESS;
      this.squashUntil = timestamp + IdleAnimation.SQUASH_MS;
    }

    applyBounceTransform(scaleX, scaleY) {
      this.bounceLogo.style.transform = `translate3d(${this.bounceX}px, ${this.bounceY}px, 0) scale(${scaleX}, ${scaleY})`;
    }

    runBounce(timestamp) {
      if (this.effect !== 'logo_bounce' || !this.classList.contains('is-active') || !this.bounceLogo) {
        return;
      }

      if (!this.lastTimestamp) this.lastTimestamp = timestamp;
      const delta = Math.min((timestamp - this.lastTimestamp) / 16.667, 3);
      this.lastTimestamp = timestamp;

      this.bounceX += this.velocityX * delta;
      this.bounceY += this.velocityY * delta;

      const maxX = Math.max(0, window.innerWidth - this.bounceLogo.offsetWidth);
      const maxY = Math.max(0, window.innerHeight - this.bounceLogo.offsetHeight);

      if (this.bounceX <= 0) {
        this.bounceX = 0;
        this.velocityX = Math.abs(this.velocityX);
        this.squish(true, timestamp);
      } else if (this.bounceX >= maxX) {
        this.bounceX = maxX;
        this.velocityX = -Math.abs(this.velocityX);
        this.squish(true, timestamp);
      }

      if (this.bounceY <= 0) {
        this.bounceY = 0;
        this.velocityY = Math.abs(this.velocityY);
        this.squish(false, timestamp);
      } else if (this.bounceY >= maxY) {
        this.bounceY = maxY;
        this.velocityY = -Math.abs(this.velocityY);
        this.squish(false, timestamp);
      }

      let scaleX = 1;
      let scaleY = 1;
      if (timestamp < this.squashUntil) {
        const t = (this.squashUntil - timestamp) / IdleAnimation.SQUASH_MS;
        const ease = t * t;
        scaleX = 1 + (this.squashX - 1) * ease;
        scaleY = 1 + (this.squashY - 1) * ease;
      }

      this.applyBounceTransform(scaleX, scaleY);
      this.bounceFrame = requestAnimationFrame((time) => this.runBounce(time));
    }

    randomBetween(min, max) {
      return Math.random() * (max - min) + min;
    }

    stampBaseWidth() {
      const mobile = window.matchMedia(IdleAnimation.BOUNCE_MOBILE_MQ).matches;
      if (mobile) return Math.round(Math.min(window.innerWidth * 0.28, 120));
      return Math.round(Math.min(window.innerWidth * 0.11, 140));
    }

    stampMinDistance(width) {
      return width * 0.85;
    }

    /* Stamped — keep pressing logos while idle; oldest drop off at STAMP_MAX. */
    startStamp() {
      this.stampContainer = this.querySelector('.idle-stamp-container');
      this.stampSource = this.querySelector('.idle-stamp-source img');
      if (!this.stampContainer || !this.stampSource) return;

      const run = () => {
        if (!this.classList.contains('is-active')) return;

        this.stampRunId = (this.stampRunId || 0) + 1;
        const runId = this.stampRunId;
        this.stampCenters = [];
        this.stampContainer.replaceChildren();
        this.createStamp(runId);
      };

      if (this.stampSource.complete && this.stampSource.naturalWidth) {
        run();
      } else {
        this.stampSource.addEventListener('load', run, { once: true });
      }
    }

    stopStamp() {
      this.stampRunId = (this.stampRunId || 0) + 1;
      clearTimeout(this.stampTimer);
      this.stampTimer = null;
      if (this.stampContainer) {
        this.stampContainer.replaceChildren();
      }
      this.stampCenters = [];
    }

    scheduleStamp(runId) {
      const delay = this.randomBetween(IdleAnimation.STAMP_MIN_DELAY, IdleAnimation.STAMP_MAX_DELAY);
      this.stampTimer = setTimeout(() => this.createStamp(runId), delay);
    }

    createStamp(runId) {
      if (runId !== this.stampRunId || !this.classList.contains('is-active') || !this.stampContainer || !this.stampSource) {
        return;
      }

      if (this.stampContainer.childElementCount >= IdleAnimation.STAMP_MAX) {
        this.stampContainer.firstElementChild.remove();
        this.stampCenters.shift();
      }

      const scale = this.randomBetween(IdleAnimation.STAMP_SCALE_MIN, IdleAnimation.STAMP_SCALE_MAX);
      const rotation = this.randomBetween(IdleAnimation.STAMP_ROTATION_MIN, IdleAnimation.STAMP_ROTATION_MAX);
      const width = this.stampBaseWidth();
      const naturalWidth = this.stampSource.naturalWidth || 1;
      const naturalHeight = this.stampSource.naturalHeight || 1;
      const height = width * (naturalHeight / naturalWidth);
      const visualWidth = width * scale;
      const visualHeight = height * scale;
      const minDistance = this.stampMinDistance(Math.min(visualWidth, visualHeight));
      const position = this.placeStamp(visualWidth, visualHeight, minDistance);

      const stamp = this.stampSource.cloneNode(true);
      stamp.removeAttribute('id');
      stamp.className = 'idle-stamp';
      stamp.style.width = `${width}px`;
      stamp.style.left = `${position.x}px`;
      stamp.style.top = `${position.y}px`;
      stamp.style.setProperty('--stamp-rotation', `${rotation}deg`);
      stamp.style.setProperty('--stamp-scale', String(scale));

      this.stampContainer.appendChild(stamp);
      this.stampCenters.push({
        x: position.x + visualWidth / 2,
        y: position.y + visualHeight / 2,
      });

      this.scheduleStamp(runId);
    }

    /* Prefer a position that is not stacked on recent stamps. */
    placeStamp(visualWidth, visualHeight, minDistance) {
      const maxX = window.innerWidth - visualWidth * 0.35;
      const maxY = window.innerHeight - visualHeight * 0.35;
      let x = this.randomBetween(-visualWidth * 0.25, Math.max(-visualWidth * 0.25, maxX));
      let y = this.randomBetween(-visualHeight * 0.25, Math.max(-visualHeight * 0.25, maxY));

      for (let attempt = 0; attempt < IdleAnimation.STAMP_PLACE_ATTEMPTS; attempt += 1) {
        const candidateX = this.randomBetween(-visualWidth * 0.25, Math.max(-visualWidth * 0.25, maxX));
        const candidateY = this.randomBetween(-visualHeight * 0.25, Math.max(-visualHeight * 0.25, maxY));
        const cx = candidateX + visualWidth / 2;
        const cy = candidateY + visualHeight / 2;
        const farEnough = this.stampCenters.every((center) => {
          const dx = center.x - cx;
          const dy = center.y - cy;
          return dx * dx + dy * dy >= minDistance * minDistance;
        });
        if (farEnough) {
          x = candidateX;
          y = candidateY;
          break;
        }
      }

      return { x, y };
    }

    svgFetchUrl(url) {
      if (!url) return '';
      if (url.startsWith('//')) url = window.location.protocol + url;
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.delete('width');
        parsed.searchParams.delete('height');
        return parsed.toString();
      } catch (error) {
        return url;
      }
    }

    cloneSvg(source, className) {
      const svg = source.cloneNode(true);
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.removeAttribute('id');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.classList.add(className);
      svg.querySelectorAll('script, foreignObject').forEach((node) => node.remove());
      return svg;
    }

    pointDistance(a, b) {
      return Math.hypot(a.x - b.x, a.y - b.y);
    }

    findSubpathJump(shape, startLength, endLength) {
      const startPoint = shape.getPointAtLength(startLength);
      let low = startLength;
      let high = endLength;

      for (let i = 0; i < 18; i += 1) {
        const middle = (low + high) / 2;
        const middlePoint = shape.getPointAtLength(middle);
        const travelled = middle - startLength;
        const chord = this.pointDistance(startPoint, middlePoint);
        if (chord > travelled * 1.15 + 0.01) {
          high = middle;
        } else {
          low = middle;
        }
      }

      return high;
    }

    /* Compound paths: dash against the longest subpath so letters draw together. */
    getLongestSubpathLength(shape) {
      let totalLength = 0;
      try {
        totalLength = shape.getTotalLength();
      } catch (error) {
        return 100;
      }

      if (!Number.isFinite(totalLength) || totalLength <= 0) return 100;
      if (shape.tagName.toLowerCase() !== 'path') return totalLength;

      const sampleCount = Math.min(5000, Math.max(800, Math.ceil(totalLength * 2)));
      const lengths = [];
      let start = 0;
      let previousLength = 0;
      let previousPoint = shape.getPointAtLength(0);

      for (let i = 1; i <= sampleCount; i += 1) {
        const length = (i / sampleCount) * totalLength;
        const point = shape.getPointAtLength(length);
        const travelled = length - previousLength;
        const chord = this.pointDistance(previousPoint, point);

        if (chord > travelled * 1.15 + 0.01) {
          const jumpAt = this.findSubpathJump(shape, previousLength, length);
          lengths.push(Math.max(0, jumpAt - start));
          start = jumpAt;
          previousLength = jumpAt;
          previousPoint = shape.getPointAtLength(jumpAt);
          continue;
        }

        previousLength = length;
        previousPoint = point;
      }

      lengths.push(Math.max(0, totalLength - start));
      return Math.max(...lengths, 100);
    }

    getRenderedScale(shape) {
      try {
        const ctm = shape.getScreenCTM();
        if (!ctm) return 1;
        const scaleX = Math.hypot(ctm.a, ctm.b);
        const scaleY = Math.hypot(ctm.c, ctm.d);
        const scale = (Math.abs(scaleX) + Math.abs(scaleY)) / 2;
        if (Number.isFinite(scale) && scale > 0) return scale;
      } catch (error) {
        /* fallback */
      }
      return 1;
    }

    getShapeColor(sourceShape) {
      const fill = sourceShape.getAttribute('fill') || sourceShape.style.fill;
      if (fill && fill !== 'none' && fill !== 'currentColor') return fill;

      const className = sourceShape.getAttribute('class');
      if (className && sourceShape.ownerSVGElement) {
        const styleTag = sourceShape.ownerSVGElement.querySelector('style');
        if (styleTag) {
          const match = styleTag.textContent.match(new RegExp(`\\.${className.split(/\s+/)[0]}\\s*\\{[^}]*fill:\\s*([^;]+)`));
          if (match && match[1].trim() !== 'none') return match[1].trim();
        }
      }

      return getComputedStyle(this).getPropertyValue('--idle-text-color').trim() || '#fff';
    }

    /* Hairline in SVG user units, scaled so a 128-wide viewBox stays ~0.3. */
    strokeWidthFor(svg) {
      const viewBox = svg.viewBox?.baseVal;
      const width = viewBox && viewBox.width > 0 ? viewBox.width : 128;
      return Math.max(0.08, width / IdleAnimation.STROKE_VIEWBOX_RATIO);
    }

    sizeDrawLogo(svg) {
      const container = this.drawContainer;
      if (!container || !svg) return;

      const viewBox = svg.viewBox?.baseVal;
      let aspectRatio = 1;
      if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
        aspectRatio = viewBox.width / viewBox.height;
      } else {
        const width = parseFloat(svg.getAttribute('width'));
        const height = parseFloat(svg.getAttribute('height'));
        if (width > 0 && height > 0) aspectRatio = width / height;
      }

      if (aspectRatio >= 4) {
        container.style.width = 'min(76vw, 980px)';
      } else if (aspectRatio >= 2) {
        container.style.width = 'min(68vw, 820px)';
      } else if (aspectRatio >= 1) {
        container.style.width = 'min(48vw, 560px)';
      } else {
        container.style.width = 'min(34vw, 420px)';
      }
    }

    drawDuration(shape, msPerPx, minMs, maxMs) {
      const drawLength = Number(shape.dataset.idleDrawLength) || 100;
      const renderedLength = drawLength * this.getRenderedScale(shape);
      return Math.max(minMs, Math.min(maxMs, renderedLength * msPerPx));
    }

    sequenceIsValid(sequenceId) {
      return sequenceId === this.drawSequenceId && this.classList.contains('is-active');
    }

    wait(ms, sequenceId) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(this.sequenceIsValid(sequenceId)), ms);
      });
    }

    showDrawError(message) {
      if (!this.drawContainer) return;
      this.drawContainer.innerHTML = `<span class="idle-experience__missing">${message}</span>`;
      this.drawReady = false;
    }

    /* Fetch SVG and build sizer + outline + fill layers before the first idle. */
    prepareDraw() {
      if (this.drawPreparePromise) return this.drawPreparePromise;

      this.drawContainer = this.querySelector('.idle-draw-logo');
      this.drawPrompt = this.querySelector('.idle-draw-prompt');
      const sourceUrl = this.svgFetchUrl(this.dataset.drawLogoUrl || '');

      if (!this.drawContainer || !sourceUrl) {
        this.showDrawError('Select an animation-ready SVG in Theme settings.');
        this.drawPreparePromise = Promise.resolve();
        return this.drawPreparePromise;
      }

      this.drawPreparePromise = fetch(sourceUrl)
        .then((response) => {
          if (!response.ok) throw new Error('Unable to load animation SVG.');
          return response.text();
        })
        .then((markup) => {
          const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml');
          const sourceSvg = parsed.querySelector('svg');
          if (!sourceSvg || parsed.querySelector('parsererror')) {
            throw new Error('SVG contains no drawable vector geometry.');
          }

          const sizingSvg = this.cloneSvg(sourceSvg, 'idle-draw-logo-sizer');
          const outlineSvg = this.cloneSvg(sourceSvg, 'idle-draw-outline-svg');
          const fillSvg = this.cloneSvg(sourceSvg, 'idle-draw-fill-svg');
          const outlineShapes = Array.from(outlineSvg.querySelectorAll(IdleAnimation.DRAWABLE));
          const sourceShapes = Array.from(sourceSvg.querySelectorAll(IdleAnimation.DRAWABLE));

          if (!outlineShapes.length) {
            throw new Error('SVG contains no drawable vector geometry.');
          }

          this.drawContainer.innerHTML = '';
          this.drawContainer.append(sizingSvg, outlineSvg, fillSvg);
          this.sizeDrawLogo(sizingSvg);
          outlineSvg.getBoundingClientRect();

          const strokeWidth = this.strokeWidthFor(outlineSvg);
          outlineShapes.forEach((shape, index) => {
            const sourceShape = sourceShapes[index] || shape;
            shape.classList.add('idle-draw-outline-shape');
            shape.style.setProperty('--idle-shape-color', this.getShapeColor(sourceShape));
            shape.style.setProperty('--idle-stroke-width', String(strokeWidth));
            const drawLength = this.getLongestSubpathLength(shape);
            shape.dataset.idleDrawLength = String(drawLength);
            shape.style.strokeDasharray = `${drawLength} ${drawLength}`;
            shape.style.strokeDashoffset = `${drawLength}`;
          });

          this.drawSizerSvg = sizingSvg;
          this.drawReady = true;
        })
        .catch((error) => {
          console.warn('Idle Logo Draw:', error);
          this.showDrawError(error.message || 'Unable to load animation SVG.');
        });

      return this.drawPreparePromise;
    }

    startDraw() {
      this.prepareDraw().then(() => {
        if (!this.classList.contains('is-active') || !this.drawReady) return;
        this.runDrawSequence();
      });
    }

    stopDraw() {
      this.drawSequenceId += 1;
      this.querySelectorAll('.idle-draw-outline-shape').forEach((shape) => {
        shape.getAnimations().forEach((animation) => animation.cancel());
        const drawLength = Number(shape.dataset.idleDrawLength) || 100;
        shape.style.strokeDashoffset = `${drawLength}`;
      });
      this.querySelector('.idle-draw-fill-svg')?.classList.remove('is-filled');
      this.drawPrompt?.classList.remove('is-visible');
    }

    /* draw → fill → CTA hold → reverse. drawSequenceId cancels this if the fan moves. */
    async runDrawSequence() {
      if (this.effect !== 'logo_draw' || !this.drawReady || !this.classList.contains('is-active')) {
        return;
      }

      const sequenceId = (this.drawSequenceId += 1);
      const outlineShapes = Array.from(this.querySelectorAll('.idle-draw-outline-shape'));
      const fillSvg = this.querySelector('.idle-draw-fill-svg');

      outlineShapes.forEach((shape) => {
        shape.getAnimations().forEach((animation) => animation.cancel());
        const drawLength = Number(shape.dataset.idleDrawLength) || 100;
        shape.style.strokeDasharray = `${drawLength} ${drawLength}`;
        shape.style.strokeDashoffset = `${drawLength}`;
      });
      fillSvg?.classList.remove('is-filled');
      this.drawPrompt?.classList.remove('is-visible');

      const drawAnimations = outlineShapes.map((shape) => {
        const drawLength = Number(shape.dataset.idleDrawLength) || 100;
        return shape.animate(
          [{ strokeDashoffset: `${drawLength}` }, { strokeDashoffset: '0' }],
          {
            duration: this.drawDuration(
              shape,
              IdleAnimation.DRAW_MS_PER_PX,
              IdleAnimation.DRAW_MIN_MS,
              IdleAnimation.DRAW_MAX_MS
            ),
            easing: IdleAnimation.DRAW_EASING,
            fill: 'forwards',
          }
        );
      });

      await Promise.all(drawAnimations.map((animation) => animation.finished.catch(() => {})));
      if (!this.sequenceIsValid(sequenceId)) return;

      fillSvg?.classList.add('is-filled');
      if (!(await this.wait(IdleAnimation.FILL_HANDOFF_MS, sequenceId))) return;

      this.drawPrompt?.classList.add('is-visible');
      if (!(await this.wait(IdleAnimation.CTA_HOLD_MS, sequenceId))) return;

      this.drawPrompt?.classList.remove('is-visible');
      fillSvg?.classList.remove('is-filled');
      if (!(await this.wait(IdleAnimation.PRE_REVERSE_MS, sequenceId))) return;

      const reverseAnimations = outlineShapes.map((shape) => {
        const drawLength = Number(shape.dataset.idleDrawLength) || 100;
        return shape.animate(
          [{ strokeDashoffset: '0' }, { strokeDashoffset: `${drawLength}` }],
          {
            duration: this.drawDuration(
              shape,
              IdleAnimation.REVERSE_MS_PER_PX,
              IdleAnimation.REVERSE_MIN_MS,
              IdleAnimation.REVERSE_MAX_MS
            ),
            easing: IdleAnimation.DRAW_EASING,
            fill: 'forwards',
          }
        );
      });

      await Promise.all(reverseAnimations.map((animation) => animation.finished.catch(() => {})));
      if (!this.sequenceIsValid(sequenceId)) return;
      if (!(await this.wait(IdleAnimation.LOOP_GAP_MS, sequenceId))) return;

      this.runDrawSequence();
    }
  }

  customElements.define('idle-animation', IdleAnimation);
}
